import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language } from '../../../constants/language';
import { schoolService } from '../../../services/school-service';
import { School } from '../../../models/school/school';
import { LoadingSpinner, LocationLink, VideoPlayer, UniversalEnrollButton } from '../../ui';
import { cn } from '../../../utils/class-names-util';
import {Asset} from "../../../models/assets/asset";
import { CONTACT_EN, CONTACT_AR } from '../../../models/contact/contact';

const SchoolDetail: React.FC = () => {
  const { schoolKey, schoolId } = useParams<{ schoolKey: string; schoolId: string }>();
  const { t, i18n } = useTranslation();
  const [school, setSchool] = useState<School | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
  const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [thumbnailLoadingStates, setThumbnailLoadingStates] = useState<Record<number, boolean>>({});
  const [thumbnailErrorStates, setThumbnailErrorStates] = useState<Record<number, boolean>>({});
  const [pdfLoadingStates, setPdfLoadingStates] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const fetchSchool = async () => {
      setIsLoading(true);
      try {
        let foundSchool: School | undefined;

        if (schoolId) {
          foundSchool = await schoolService.getSchoolById(parseInt(schoolId), i18n.language as Language);
        } else if (schoolKey) {
          foundSchool = await schoolService.getSchoolByKey(schoolKey, i18n.language as Language);
        }

        setSchool(foundSchool || null);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchool();
  }, [schoolKey, schoolId, i18n.language]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!school) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('pages.schools.notFound', 'School Not Found')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('pages.schools.notFoundMessage', 'The school you are looking for could not be found.')}
          </p>
          <Link
            to="/schools"
            className="bg-black text-white hover:bg-gray-800 px-6 py-3 rounded-lg font-medium transition-colors duration-300"
          >
            {t('pages.schools.backToSchools', 'Back to Schools')}
          </Link>
        </div>
      </div>
    );
  }

  const isEnrollmentOpen = school.enrollmentStatus === 'open';
  const isRTL = i18n.language === 'ar';

  const handleImageClick = (imageSrc: string) => {
    setSelectedImage(imageSrc);
  };

  const handleImageLoad = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleImageError = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
    setImageErrorStates(prev => ({ ...prev, [index]: true }));
  };

  const handleImageStart = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: true }));
    setImageErrorStates(prev => ({ ...prev, [index]: false }));
  };


  const handleThumbnailLoad = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleThumbnailError = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: false }));
    setThumbnailErrorStates(prev => ({ ...prev, [index]: true }));
  };

  const handleThumbnailStart = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: true }));
    setThumbnailErrorStates(prev => ({ ...prev, [index]: false }));
  };

  const handleVideoChange = (index: number) => {
    setActiveVideoIndex(index);
    setVideoLoading(true);
    setVideoError(false);

    setThumbnailLoadingStates(prev => {
      const newStates = { ...prev };
      if (!(index in newStates)) {
        newStates[index] = true;
      }
      return newStates;
    });
  };

  const isValidVideoUrl = (url?: string): boolean => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handlePdfClick = (pdfUrl: string, index: number) => {
    setPdfLoadingStates(prev => ({ ...prev, [index]: true }));
    setSelectedPdf(pdfUrl);
    setTimeout(() => {
      setPdfLoadingStates(prev => ({ ...prev, [index]: false }));
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16 lg:py-24">
        <div className="absolute inset-0 opacity-10">
          <img
            src={school.media.cover?.url || ''}
            alt={school.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-4 text-sm mb-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">
              {t('nav.home', 'Home')}
            </Link>
            <span className="text-gray-500">/</span>
            <Link to="/schools" className="text-gray-300 hover:text-white transition-colors">
              {t('nav.schools', 'Schools')}
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-white">{school.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                  <img
                    src={school.media.logo?.url || ''}
                    alt={school.title}
                    className="w-12 h-12 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div class="w-12 h-12 bg-black/20 rounded-lg flex items-center justify-center text-black text-lg font-bold">${school.title.charAt(0)}</div>`;
                      }
                    }}
                  />
                </div>
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <span
                      className={cn(
                        "inline-block px-3 py-1 text-xs font-medium rounded-full",
                        isEnrollmentOpen
                          ? "bg-black text-white"
                          : "bg-red-500 text-white"
                      )}
                    >
                      {isEnrollmentOpen ? t('pages.schools.enrollmentOpen', 'Enrollment Open') : t('pages.schools.enrollmentClosed', 'Enrollment Closed')}
                    </span>
                    <span className="bg-white/10 text-white px-3 py-1 text-xs font-medium rounded-full">
                      {school.category}
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                    {school.title}
                  </h1>
                </div>
              </div>

              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                {school.description}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{school.duration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  <LocationLink address={school.address} />
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{school.startDate}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <UniversalEnrollButton
                  enrollmentUrl={school.enrollmentForm}
                  variant="secondary"
                  size="lg"
                  className="w-full py-3 px-6 text-lg font-semibold mb-4"
                  disabled={!isEnrollmentOpen}
                >
                  {isEnrollmentOpen ? t('pages.schools.enrollNow', 'Enroll Now') : t('pages.schools.enrollmentClosed', 'Enrollment Closed')}
                </UniversalEnrollButton>

                {school.contactInfo && (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${school.contactInfo.EMAIL}`} className="text-gray-300 hover:text-white">
                        {school.contactInfo.EMAIL}
                      </a>
                    </div>
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-gray-300">{school.contactInfo.PHONE}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div>
                <h2 className="text-3xl font-bold text-black mb-6">
                  {t('pages.schools.overview', 'Overview')}
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 leading-relaxed">
                    {school.longDescription}
                  </p>
                </div>
              </div>

              {/* Curriculum */}
              {school.curriculum && (
                <div>
                  <h2 className="text-3xl font-bold text-black mb-6">
                    {t('pages.schools.curriculum', 'Curriculum')}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {school.curriculum.map((item, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                        <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs font-bold">{index + 1}</span>
                        </div>
                        <span className="text-gray-700 font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Video Section */}
              {school.media.videos && school.media.videos.length > 0 && isValidVideoUrl(school.media.videos[activeVideoIndex]?.url) && (
                <section>
                  <h2 className="text-3xl font-bold mb-6">{t('general.video.title')}</h2>
                  <div className="bg-black rounded-2xl overflow-hidden mb-4 relative">
                    <div className="aspect-video">
                      {videoLoading && (
                        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center text-white z-10">
                          <div className="flex flex-col items-center space-y-4">
                            <svg className="w-12 h-12 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div className="text-sm">{t('ui.loading', 'Loading video...')}</div>
                          </div>
                        </div>
                      )}

                      {videoError ? (
                        <div className="w-full h-full bg-gray-900 flex items-center justify-center text-white">
                          <div className="text-center">
                            <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                              <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="text-xl mb-2">Error loading video</div>
                            <div className="text-sm text-gray-400 mb-4 max-w-md truncate">
                              {school.media.videos[activeVideoIndex]?.url}
                            </div>
                            <div className="space-y-2">
                              <button
                                onClick={() => {
                                  setVideoError(false);
                                  setVideoLoading(true);
                                }}
                                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors mr-2"
                              >
                                Retry
                              </button>
                              <a
                                href={school.media.videos[activeVideoIndex]?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 px-4 py-2 rounded-lg transition-colors inline-block"
                              >
                                Open in New Tab
                              </a>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <VideoPlayer
                          key={school.media.videos[activeVideoIndex]?.url}
                          url={school.media.videos[activeVideoIndex]?.url || ''}
                          className="w-full h-full"
                          poster={school.media.videos[activeVideoIndex]?.thumbnailUrl}
                          onLoadStart={() => {
                            setVideoLoading(true);
                            setVideoError(false);
                          }}
                          onCanPlay={() => {
                            setVideoLoading(false);
                          }}
                          onError={() => {
                            setVideoLoading(false);
                            setVideoError(true);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {school.media.videos.length > 1 && (
                    <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3 overflow-x-auto pb-2 mb-4`}>
                      {school.media.videos.map((video: Asset, index: number) => (
                        <button
                          key={index}
                          onClick={() => handleVideoChange(index)}
                          className={`flex-shrink-0 relative w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            activeVideoIndex === index 
                              ? 'border-black shadow-lg' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {video.thumbnailUrl ? (
                            <div className="relative w-full h-full">
                              {thumbnailLoadingStates[index] && (
                                <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                                  <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              )}

                              {thumbnailErrorStates[index] && (
                                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              )}

                              <img
                                src={video.thumbnailUrl}
                                alt={`Video ${index + 1} thumbnail`}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${
                                  thumbnailLoadingStates[index] ? 'opacity-0' : 'opacity-100'
                                }`}
                                onLoadStart={() => handleThumbnailStart(index)}
                                onLoad={() => handleThumbnailLoad(index)}
                                onError={() => handleThumbnailError(index)}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}

                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <div className={`w-6 h-6 flex items-center justify-center rounded-full ${
                              activeVideoIndex === index ? 'bg-white text-black' : 'bg-white/80 text-black'
                            }`}>
                              <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                          {activeVideoIndex === index && (
                            <div className="absolute inset-0 border-2 border-black rounded-lg pointer-events-none"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* PDF Resources Section */}
              {school.media.files && school.media.files.length > 0 && (
                <section>
                  <h2 className="text-3xl font-bold mb-6">{t('general.pdf.title')}</h2>
                  <div className="space-y-4">
                    {school.media.files.map((pdf, index) => (
                      <button
                        key={index}
                        onClick={() => handlePdfClick(pdf.url, index)}
                        disabled={pdfLoadingStates[index]}
                        className="w-full flex items-center space-x-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group text-left disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                          {pdfLoadingStates[index] ? (
                            <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 group-hover:text-black">
                            {pdfLoadingStates[index] ? t('ui.loading', 'Loading...') : (pdf.name || `Document ${index + 1}`)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {pdfLoadingStates[index] 
                              ? t('general.pdf.loading', 'Loading PDF...')
                              : `${t('pages.schools.pdfDocument', 'PDF Document')} - ${t('pages.schools.clickToView', 'Click to view')}`
                            }
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Testimonials - Vertical Carousel */}
              {school.testimonials && school.testimonials.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-black mb-6">
                    {t('pages.schools.testimonials', 'Student Testimonials')}
                  </h2>
                  <div className="relative">
                    <div className="bg-gray-50 rounded-lg p-8 mb-4">
                      <div className="flex items-start space-x-6 mb-6">
                        <div className="w-20 h-20 bg-gray-300 rounded-full overflow-hidden flex-shrink-0">
                          <img
                            src="/images/testimonials/placeholder-avatar.jpg"
                            alt={school.testimonials?.[currentImageIndex]?.studentName || 'Student'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent && school.testimonials && school.testimonials[currentImageIndex]) {
                                const initial = school.testimonials[currentImageIndex].studentName.charAt(0) || 'S';
                                parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-600 font-bold text-xl">${initial}</div>`;
                              }
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-semibold text-gray-900 text-lg">
                                {school.testimonials?.[currentImageIndex]?.studentName}
                              </div>
                              {school.testimonials?.[currentImageIndex]?.graduation && (
                                <div className="text-sm text-gray-500">
                                  {school.testimonials[currentImageIndex]?.graduation}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <svg
                                  key={i}
                                  className={cn(
                                    'w-5 h-5',
                                    i < (school.testimonials?.[currentImageIndex]?.rating || 0)
                                      ? 'text-yellow-400'
                                      : 'text-gray-300'
                                  )}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                          </div>
                          <blockquote className="text-gray-700 italic text-lg leading-relaxed">
                            "{school.testimonials?.[currentImageIndex]?.message}"
                          </blockquote>
                        </div>
                      </div>
                    </div>

                    {/* Navigation Controls */}
                    {school.testimonials && school.testimonials.length > 1 && (
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : (school.testimonials?.length || 1) - 1)}
                          className={`p-3 rounded-full bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all ${isRTL ? 'rotate-180' : ''}`}
                          title={t('navigation.previous', 'Previous testimonial')}
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        <div className="flex items-center space-x-2">
                          {school.testimonials.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-3 h-3 rounded-full transition-all ${
                                index === currentImageIndex
                                  ? 'bg-black'
                                  : 'bg-gray-300 hover:bg-gray-400'
                              }`}
                              title={`${t('navigation.goTo', 'Go to')} ${index + 1}`}
                            />
                          ))}
                          <span className="ml-4 text-sm text-gray-500">
                            {currentImageIndex + 1} / {school.testimonials?.length || 0}
                          </span>
                        </div>

                        <button
                          onClick={() => setCurrentImageIndex(currentImageIndex < (school.testimonials?.length || 1) - 1 ? currentImageIndex + 1 : 0)}
                          className={`p-3 rounded-full bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all ${isRTL ? 'rotate-180' : ''}`}
                          title={t('navigation.next', 'Next testimonial')}
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-8">
                {/* School Info Card */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-black mb-4">
                    {t('pages.schools.schoolInfo', 'School Information')}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <div>
                      <div className="text-gray-500 mb-1">{t('pages.schools.duration', 'Duration')}</div>
                      <div className="font-medium">{school.duration}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-1">{t('pages.schools.schedule', 'Schedule')}</div>
                      <div className="font-medium">{school.time}</div>
                    </div>
                    {/* Contact Information for Details */}
                    <div className="col-span-2 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="text-gray-700 mb-3">
                        {t('pages.schools.contactForDetails', 'For more details contact us')}
                      </div>
                      <div className="flex flex-col space-y-2 text-sm">
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <a 
                            href={`mailto:${isRTL ? CONTACT_AR.EMAIL : CONTACT_EN.EMAIL}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {isRTL ? CONTACT_AR.EMAIL : CONTACT_EN.EMAIL}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <a 
                            href={`tel:${isRTL ? CONTACT_AR.PHONE : CONTACT_EN.PHONE}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {isRTL ? CONTACT_AR.PHONE : CONTACT_EN.PHONE}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Photo Gallery Carousel */}
                {school.media.photos && school.media.photos.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-bold mb-4">📸 {t('general.gallery.title')}</h3>
                    <div className="relative">
                      {/* Main carousel container */}
                      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        <button
                          onClick={() => handleImageClick(school.media.photos[currentImageIndex]?.url || '')}
                          className="w-full h-full flex items-center justify-center hover:opacity-90 transition-opacity group relative"
                        >
                          {/* Loading placeholder */}
                          {imageLoadingStates[currentImageIndex] && (
                            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center z-10">
                              <div className="flex flex-col items-center space-y-3">
                                <svg className="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <div className="text-xs text-gray-500">{t('ui.loading', 'Loading...')}</div>
                              </div>
                            </div>
                          )}

                          {/* Error placeholder */}
                          {imageErrorStates[currentImageIndex] && (
                            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
                              <div className="flex flex-col items-center space-y-2 text-gray-400">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <div className="text-xs">Image unavailable</div>
                              </div>
                            </div>
                          )}

                          <img
                            src={school.media.photos[currentImageIndex]?.url || ''}
                            alt={`${school.title} - ${currentImageIndex + 1}`}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoadingStates[currentImageIndex] ? 'opacity-0' : 'opacity-100'}`}
                            onLoadStart={() => handleImageStart(currentImageIndex)}
                            onLoad={() => handleImageLoad(currentImageIndex)}
                            onError={() => handleImageError(currentImageIndex)}
                          />
                          
                          {/* Click to expand overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-black px-4 py-2 rounded-lg font-medium">
                              {t('general.gallery.click-to-enlarge', 'Click to enlarge')}
                            </div>
                          </div>
                        </button>

                        {/* Navigation arrows */}
                        {school.media.photos.length > 1 && (
                          <>
                            <button
                              onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : school.media.photos.length - 1)}
                              className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10`}
                            >
                              <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setCurrentImageIndex(currentImageIndex < school.media.photos.length - 1 ? currentImageIndex + 1 : 0)}
                              className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10`}
                            >
                              <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>

                      {/* Thumbnail navigation */}
                      {school.media.photos.length > 1 && (
                        <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-2 mt-4 overflow-x-auto pb-2`}>
                          {school.media.photos.map((photo: Asset, index: number) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                index === currentImageIndex
                                  ? 'border-black shadow-md'
                                  : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              <img
                                src={photo?.url || ''}
                                alt={`${school.title} - ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = `<div class="w-full h-full bg-gray-100 flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>`;
                                  }
                                }}
                              />
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Image counter */}
                      {school.media.photos.length > 1 && (
                        <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {currentImageIndex + 1} / {school.media.photos.length}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Instructors */}
                {school.instructors && school.instructors.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-black mb-4">
                      {t('pages.schools.instructors', 'Instructors')}
                    </h3>
                    <div className="space-y-4">
                      {school.instructors.map((instructor) => (
                        <div key={instructor.id} className="flex items-start space-x-3">
                          <div className="w-12 h-12 bg-gray-300 rounded-full overflow-hidden flex-shrink-0">
                            <img
                              src={instructor.photo}
                              alt={instructor.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-600 font-bold">${instructor.name.charAt(0)}</div>`;
                                }
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">{instructor.name}</div>
                            <div className="text-xs text-gray-500 mb-1">{instructor.title}</div>
                            <div className="text-xs text-gray-600 line-clamp-2">{instructor.bio}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prerequisites */}
                {school.prerequisites && school.prerequisites.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-black mb-4">
                      {t('pages.schools.prerequisites', 'Prerequisites')}
                    </h3>
                    <ul className="space-y-2 text-sm">
                      {school.prerequisites.map((prerequisite, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 bg-black rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-600">{prerequisite}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className={`flex items-center justify-between mb-8`}>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {t('pages.schools.enrollNow', 'Enroll Now')}
                  </h2>
                  <p className="text-gray-600">
                    {t('pages.schools.joinSchoolTransform', 'Join {{title}} and transform your ministry calling', { title: school.title })}
                  </p>
                </div>
                <button
                  onClick={() => setShowEnrollModal(false)}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {t('pages.schools.fullName', 'Full Name')} *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-gray-800"
                      placeholder={t('pages.schools.enterFullName', 'Enter your full name')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {t('pages.schools.emailAddress', 'Email Address')} *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-gray-800"
                      placeholder={t('pages.schools.enterEmail', 'your.email@example.com')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {t('pages.schools.phoneNumber', 'Phone Number')} *
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-gray-800"
                      placeholder={t('pages.schools.enterPhone', '+20 123 456 7890')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      {t('pages.schools.ageRange', 'Age Range')}
                    </label>
                    <select className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-gray-800 bg-white">
                      <option value="">{t('pages.schools.selectAgeRange', 'Select age range')}</option>
                      <option value="18-25">{t('pages.schools.ageRanges.18-25', '18-25 years')}</option>
                      <option value="26-35">{t('pages.schools.ageRanges.26-35', '26-35 years')}</option>
                      <option value="36-50">{t('pages.schools.ageRanges.36-50', '36-50 years')}</option>
                      <option value="51+">{t('pages.schools.ageRanges.51+', '51+ years')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    {t('pages.schools.previousMinistryExperience', 'Previous Ministry Experience')}
                  </label>
                  <select className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-gray-800 bg-white">
                    <option value="">{t('pages.schools.selectExperience', 'Select your experience level')}</option>
                    <option value="new">{t('pages.schools.newToMinistry', 'New to ministry')}</option>
                    <option value="some">{t('pages.schools.someMinistryBackground', 'Some ministry background')}</option>
                    <option value="experienced">{t('pages.schools.experiencedInMinistry', 'Experienced in ministry')}</option>
                    <option value="leader">{t('pages.schools.leadershipExperience', 'Leadership experience')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    {t('pages.schools.whatDrawsYou', 'What draws you to this school?')} *
                  </label>
                  <textarea
                    required
                    rows={4}
                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-gray-800 resize-none"
                    placeholder={t('pages.schools.shareYourHeart', 'Share your heart and what you hope to gain from this school experience...')}
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                    <input
                      type="checkbox"
                      required
                      className="mt-1 w-5 h-5 text-black border-2 border-gray-300 rounded focus:ring-black"
                    />
                    <span className="text-sm text-gray-700">
                      {t('pages.schools.enrollmentCommitment', 'I understand the commitment required for this school and agree to participate faithfully. I also consent to being contacted by')} <span className="font-bold text-black shadow-sm px-1 py-0.5 rounded bg-gray-50 border border-gray-200">{t('pages.schools.organizationName', 'ZOE For All Nations')}</span> {t('pages.schools.regardingEnrollment', 'regarding this enrollment.')}
                    </span>
                  </label>
                </div>

                <div className={`flex flex-col sm:flex-row space-y-3 sm:space-y-0 ${isRTL ? 'sm:space-x-reverse' : ''} sm:space-x-4 pt-6`}>
                  <button
                    type="button"
                    onClick={() => setShowEnrollModal(false)}
                    className="flex-1 bg-gray-100 text-gray-800 hover:bg-gray-200 py-4 px-6 rounded-lg transition-colors font-semibold text-lg"
                  >
                    {t('ui.buttons.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-black text-white hover:bg-gray-800 py-4 px-6 rounded-lg transition-colors font-semibold text-lg shadow-lg"
                  >
                    {t('ui.buttons.submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={selectedImage}
              alt={t('pages.schools.schoolGallery', 'School gallery')}
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {selectedPdf && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedPdf(null)}
        >
          <div className="relative w-full max-w-4xl h-full max-h-[90vh] bg-white rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('pages.schools.pdfViewer', 'PDF Viewer')}</h3>
              <button
                onClick={() => setSelectedPdf(null)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              src={selectedPdf}
              className="w-full h-full"
              title={t('pages.schools.pdfDocument', 'PDF Document')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolDetail;